import { Menu } from 'lucide-react'
import { MOCK_PROVIDERS } from '@/lib/catalog/mock-data'
import type { CatalogFilterStatus, CatalogFilters } from '@/types/catalog'
import { cn } from '@/lib/utils'

interface ModelFilterBarProps {
  filters: CatalogFilters
  onChange: (patch: Partial<CatalogFilters>) => void
  onOpenCategories?: () => void
  showCategoriesButton?: boolean
  className?: string
}

const STATUS_OPTIONS: { value: CatalogFilterStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'pinned', label: 'Pinned' },
  { value: 'archived', label: 'Archived' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'blacklisted', label: 'Blacklisted' },
  { value: 'all', label: 'All' },
]

export function ModelFilterBar({
  filters,
  onChange,
  onOpenCategories,
  showCategoriesButton = false,
  className,
}: ModelFilterBarProps) {
  const toggleProvider = (providerId: string) => {
    const selected = filters.providers.includes(providerId)
    onChange({
      providers: selected
        ? filters.providers.filter((id) => id !== providerId)
        : [...filters.providers, providerId],
    })
  }

  return (
    <div
      className={cn('space-y-3 border-b border-border bg-background p-3', className)}
      data-testid="catalog-filter-bar"
    >
      <div className="flex flex-wrap items-center gap-2">
        {showCategoriesButton && onOpenCategories ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm hover:bg-muted"
            onClick={onOpenCategories}
            data-testid="catalog-categories-drawer-trigger"
          >
            <Menu className="h-4 w-4" />
            Categories
          </button>
        ) : null}

        <label className="inline-flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Status</span>
          <select
            className="rounded-md border border-input bg-background px-2 py-1"
            value={filters.status}
            onChange={(event) =>
              onChange({ status: event.target.value as CatalogFilterStatus })
            }
            data-testid="catalog-filter-status"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.freeOnly}
            onChange={(event) => onChange({ freeOnly: event.target.checked })}
            data-testid="catalog-filter-free-only"
          />
          Free only
        </label>

        <label className="inline-flex min-w-[180px] flex-1 items-center gap-2 text-sm">
          <span className="whitespace-nowrap text-muted-foreground">Max price / M</span>
          <input
            type="range"
            min={0}
            max={10}
            step={0.5}
            value={filters.maxPrice ?? 10}
            onChange={(event) => {
              const value = Number(event.target.value)
              onChange({ maxPrice: value >= 10 ? null : value })
            }}
            className="w-full"
            data-testid="catalog-filter-max-price"
          />
          <span className="w-10 text-right font-mono text-xs">
            {filters.maxPrice === null ? '∞' : `$${filters.maxPrice}`}
          </span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="catalog-filter-providers">
        {MOCK_PROVIDERS.map((provider) => {
          const active = filters.providers.includes(provider.id)
          return (
            <button
              key={provider.id}
              type="button"
              onClick={() => toggleProvider(provider.id)}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border hover:bg-muted',
              )}
            >
              {provider.name}
            </button>
          )
        })}
      </div>
    </div>
  )
}
